import React, { useEffect, useState } from 'react';

import { Alert, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { CommissionsConditionTypes, RuleCondition } from '~/consts/agentHub';

import { Dayjs, SYDNEY_TIMEZONE } from '~/services/TimeService';

import { formatSydneyDateOnly, parseSydneyDateOnly } from '~/utils/commissionDates';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  conditionType: CommissionsConditionTypes;
  existingConditions: RuleCondition['value'];
  description?: string;
}

function AgentHubCommissionsDateRangeInput({
  setCreatedConditions,
  conditionType,
  existingConditions,
  description,
}: Props) {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string>('');

  // Initialize dates from existing conditions if available
  useEffect(() => {
    if (existingConditions && existingConditions.length >= 2) {
      try {
        const start = parseSydneyDateOnly(String(existingConditions[0].value));
        const end = parseSydneyDateOnly(String(existingConditions[1].value));
        if (start.isValid()) {
          setStartDate(start);
        }
        if (end.isValid()) {
          setEndDate(end);
        }
      } catch {
        // Silently fail if dates can't be parsed
      }
    }
  }, [existingConditions]);

  // Validate and save when both dates are set
  useEffect(() => {
    if (!startDate && !endDate) {
      setError('');
      return;
    }

    if (startDate && endDate) {
      if (endDate.isBefore(startDate)) {
        setError('End date cannot be before start date');
        return;
      }
      const formattedStartDate = formatSydneyDateOnly(startDate);
      const formattedEndDate = formatSydneyDateOnly(endDate);

      setError('');
      setCreatedConditions(
        {
          type: conditionType,
          value: [
            { value: formattedStartDate, label: formattedStartDate },
            { value: formattedEndDate, label: formattedEndDate },
          ],
        },
        true,
      );
    }
  }, [conditionType, endDate, setCreatedConditions, startDate]);

  const handleStartDateChange = (newDate: Dayjs | null) => {
    if (newDate && endDate && newDate.isAfter(endDate)) {
      setError('Start date cannot be after end date');
    } else {
      setError('');
    }
    setStartDate(newDate);
  };

  const handleEndDateChange = (newDate: Dayjs | null) => {
    if (newDate && startDate && newDate.isBefore(startDate)) {
      setError('End date cannot be before start date');
    } else {
      setError('');
    }
    setEndDate(newDate);
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1.5}>
        <DatePicker
          label="Start date"
          value={startDate}
          onChange={handleStartDateChange}
          format="DD/MM/YYYY"
          timezone={SYDNEY_TIMEZONE}
          slotProps={{
            textField: {
              fullWidth: true,
              InputProps: { sx: { borderRadius: 0.5 } },
            },
          }}
        />
        <DatePicker
          label="End date"
          value={endDate}
          onChange={handleEndDateChange}
          format="DD/MM/YYYY"
          timezone={SYDNEY_TIMEZONE}
          slotProps={{
            textField: {
              fullWidth: true,
              InputProps: { sx: { borderRadius: 0.5 } },
            },
          }}
          minDate={startDate || undefined}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ borderRadius: 0.5 }}>
          {error}
        </Alert>
      )}

      <Typography variant="caption" color="textSecondary">
        Select both start and end dates. End date must be greater than or equal to the start date.
      </Typography>

      {description && (
        <Typography variant="caption" color="textSecondary">
          {description}
        </Typography>
      )}
    </Stack>
  );
}

export default AgentHubCommissionsDateRangeInput;
